import React, { useMemo } from 'react';
import { GoogleAdsRequestGq } from '../../../../graphql/projects/models/GoogleAdsRequestGq';
import { KeywordFilteringGq } from '../../../../graphql/projects/models/KeywordFilteringGq';
import { KeywordSortByTypeGq } from '../../../../graphql/projects/models/KeywordSortByTypeGq';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { zIndexVoxel } from '../../../../logic/zIndexVoxel';
import VoxelDataTableHeadCell from '../../../voxel/DataTable/VoxelDataTableHeadCell';
import VoxelDataTableRow from '../../../voxel/DataTable/VoxelDataTableRow';
import VoxelTableCheckbox from '../../../voxel/Forms/VoxelTableCheckbox';
import VoxelTooltipShort from '../../../voxel/Tooltip/VoxelTooltipShort';
import HeadCellBasic from '../Shared/HeadCellBasic';
import HeadCellNumber from '../Shared/HeadCellNumber';
import HeadCellSelect from '../Shared/HeadCellSelect';
import HeadCellTagGroup from '../Shared/HeadCellTagGroup';
import HeadCellText from '../Shared/HeadCellText';
import AddFieldButton from './AddFieldButton';
import HeadCellArchiveTime from './HeadCells/HeadCellArchiveTime';
import HeadCellDomain from './HeadCells/HeadCellDomain';
import HeadCellIndirectSeedType from './HeadCells/HeadCellIndirectSeedType';
import HeadCellKeyword from './HeadCells/HeadCellKeyword';
import HeadCellRankFetchStatus from './HeadCells/HeadCellRankFetchStatus';
import HeadCellRequest from './HeadCells/HeadCellRequest';
import { HeadCellBasicState } from './HeadCells/models';
import { KeywordTablePage } from './KeywordTable';
import { getColumnId } from './KeywordTableColumnPicker/getColumnId';
import { getColumnName } from './KeywordTableColumnPicker/getColumnName';
import { KeywordTableColumn, KeywordTableColumnTypes } from './KeywordTableColumnPicker/models';
import { KeywordTableState } from './logic/useKeywordTableState';
import { getColumnDescription } from './KeywordTableColumnPicker/getColumnDescription';
import { usersApiService } from '../../../../graphql/users/usersApiService';
import { FrontendUserEventGq } from '../../../../graphql/users/queries/useFrontendLogUserEventGq';
import { chain } from 'lodash';
import moment from 'moment';
import {
  IndirectSeedTypeGq,
  indirectSeedTypeToString,
} from '../../../../graphql/projects/models/IndirectSeedTypeGq';
import { GenericOption } from '../../../voxel/Forms/VoxelAutocomplete';

export interface HeadRowProps {
  project?: ProjectGq;
  keywordTableState: KeywordTableState;
  requests?: Record<number, GoogleAdsRequestGq>;
  shownColumns: KeywordTableColumn[];
  isProgressPage?: boolean;
  page?: KeywordTablePage;
  tableContainerRef?: HTMLDivElement;
}

const HeadRow: React.FC<HeadRowProps> = React.memo(
  ({
    project,
    keywordTableState,
    requests,
    shownColumns,
    isProgressPage,
    page,
    tableContainerRef,
  }) => {
    const { selectionState, setSelectionState } = keywordTableState;

    const getSortDirection = (sortType: KeywordSortByTypeGq, sortById?: number) =>
      keywordTableState.sortingState.sortByType === sortType &&
      keywordTableState.sortingState.sortById === sortById
        ? keywordTableState.sortingState.sortDirection
        : undefined;

    const handleFilter = (filter: Partial<KeywordFilteringGq>) =>
      keywordTableState.setFilterState((prevFilter) => ({ ...prevFilter, ...filter }));

    const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();
    const handleHideColumn = (column: KeywordTableColumnTypes, id?: number) => {
      logUserEventMutation({
        event: FrontendUserEventGq.TABLE_QUICK_HIDE_COLUMN,
      });
      keywordTableState.columnPickerBag.setColumnState((prev) =>
        prev.map((p) => {
          if (p.type === column && p.id == id) {
            return { ...p, order: undefined };
          }
          return p;
        })
      );
    };

    const getBasicState = (
      column: KeywordTableColumn,
      sortingType?: KeywordSortByTypeGq
    ): HeadCellBasicState => ({
      tableContainerRef,
      showColumn: !!column.order,
      setColumnWidth: (width: number) =>
        keywordTableState.columnPickerBag.setColumnState((prev) =>
          prev.map((p) => {
            if (p.type === column.type && p.id == column.id) {
              return { ...p, sizePx: width };
            }
            return p;
          })
        ),
      hideColumn: () => handleHideColumn(column.type, column.id),
      sortDirectionState: !sortingType ? undefined : getSortDirection(sortingType, column.id),
      toggleSortDirection: !sortingType
        ? undefined
        : (state) => keywordTableState.handleSort(sortingType, column.id, state),
      setColumnRef: (element) =>
        (keywordTableState.columnHeadRef.current[getColumnId(column)] = element),
    });

    const last12Months = useMemo(() => {
      const endDate = moment.utc(project.keywordMonth12End).add(-12, 'months');
      return chain([...Array(12).keys()])
        .map((v, index) => endDate.add(1, 'months').format('YYYY MMM'))
        .value();
    }, [project.keywordMonth12End]);

    return (
      <VoxelDataTableRow
        className={` ${zIndexVoxel.tableStickyHeadRow} sticky top-0
       `}
      >
        <VoxelDataTableHeadCell align="center">
          <VoxelTableCheckbox
            checked={selectionState.invertSelection}
            color="slate"
            onChange={() =>
              setSelectionState((prevState) => ({
                keywordIds: [],
                invertSelection: !prevState.invertSelection,
                emptyIsAll: true,
              }))
            }
          />
        </VoxelDataTableHeadCell>
        {shownColumns.map((column) => {
          if (!column) return null;
          if (!(column as any).type && (column as any).type !== 0) return null;

          const key = getColumnId(column);
          const name = getColumnName(column, project);

          if (column.type === KeywordTableColumnTypes.ArchiveTime)
            return (
              <HeadCellArchiveTime
                key={key}
                keywordTableState={keywordTableState}
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.ARCHIVE_TIME)}
              />
            );
          if (column.type === KeywordTableColumnTypes.Keyword)
            return (
              <HeadCellKeyword
                key={key}
                keywordTableState={keywordTableState}
                basicConfig={{
                  title: name,
                  hideColumnDisabled: true,
                  showSorting: true,
                  sortingType: 'text',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={{
                  ...getBasicState(column, KeywordSortByTypeGq.KEYWORD),
                  showColumn: true,
                }}
              />
            );
          if (column.type === KeywordTableColumnTypes.EnglishTranslation)
            return (
              <HeadCellText
                key={key}
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'text',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.ENGLISH)}
                filterState={keywordTableState.filterState.translationEnglish?.searchTerm ?? ''}
                setFilterState={(value) =>
                  handleFilter({ translationEnglish: { searchTerm: value } })
                }
              />
            );

          if (column.type === KeywordTableColumnTypes.Volume)
            return (
              <HeadCellNumber
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.VOLUME)}
                key={key}
                minFilterState={keywordTableState.filterState.volumeMin}
                maxFilterState={keywordTableState.filterState.volumeMax}
                setMinFilterState={(value) => handleFilter({ volumeMin: value })}
                setMaxFilterState={(value) => handleFilter({ volumeMax: value })}
              />
            );

          if (column.type === KeywordTableColumnTypes.VolumeTrend)
            return (
              <HeadCellNumber
                basicConfig={{
                  title: name,
                  showSorting: project?.createdTime > '2023-02-24 17:00',
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.TREND)}
                key={key}
                minFilterState={keywordTableState.filterState.trendMin}
                maxFilterState={keywordTableState.filterState.trendMax}
                setMinFilterState={(value) => handleFilter({ trendMin: value })}
                setMaxFilterState={(value) => handleFilter({ trendMax: value })}
                allowNegative
              />
            );

          if (column.type === KeywordTableColumnTypes.VolumeMonthPeak)
            return (
              <HeadCellSelect
                key={key}
                align="center"
                basicConfig={{
                  title: name,
                  showSorting: project?.createdTime > '2023-02-24 17:00',
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.PEAK_MONTH)}
                state={keywordTableState.filterState.peakMonths}
                setState={(selectedOptions) =>
                  keywordTableState.setFilterState((prev) => ({
                    ...prev,
                    peakMonths: selectedOptions as any,
                  }))
                }
                options={[
                  { title: 'None', value: -1 },
                  { title: 'Jan', value: 1 },
                  { title: 'Feb', value: 2 },
                  { title: 'Mar', value: 3 },
                  { title: 'Apr', value: 4 },
                  { title: 'May', value: 5 },
                  { title: 'Jun', value: 6 },
                  { title: 'Jul', value: 7 },
                  { title: 'Aug', value: 8 },
                  { title: 'Sep', value: 9 },
                  { title: 'Okt', value: 10 },
                  { title: 'Nov', value: 11 },
                  { title: 'Dec', value: 12 },
                ]}
              />
            );

          if (column.type === KeywordTableColumnTypes.VolumeQuarterPeak)
            return (
              <HeadCellSelect
                key={key}
                align="center"
                basicConfig={{
                  title: name,
                  showSorting: project?.createdTime > '2023-02-24 17:00',
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.PEAK_QUARTER)}
                state={keywordTableState.filterState.peakQuarters}
                setState={(selectedOptions) =>
                  keywordTableState.setFilterState((prev) => ({
                    ...prev,
                    peakQuarters: selectedOptions as any,
                  }))
                }
                options={[
                  { title: 'None', value: -1 },
                  { title: 'Q1', value: 1 },
                  { title: 'Q2', value: 2 },
                  { title: 'Q3', value: 3 },
                  { title: 'Q4', value: 4 },
                ]}
              />
            );

          if (column.type === KeywordTableColumnTypes.VolumeLast12months)
            return last12Months.map((my, index) => (
              <HeadCellBasic
                key={index}
                align="right"
                basicConfig={{
                  title: my,
                  disableResize: true,
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, undefined)}
              />
            ));

          if (column.type === KeywordTableColumnTypes.Cpc)
            return (
              <HeadCellNumber
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.CPC)}
                key={key}
                minFilterState={keywordTableState.filterState.cpcMin}
                maxFilterState={keywordTableState.filterState.cpcMax}
                setMinFilterState={(value) => handleFilter({ cpcMin: value })}
                setMaxFilterState={(value) => handleFilter({ cpcMax: value })}
              />
            );

          if (column.type === KeywordTableColumnTypes.CpcBidHigh)
            return (
              <HeadCellBasic
                key={key}
                align="right"
                basicConfig={{
                  title: name,
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, undefined)}
              >
                {name}
              </HeadCellBasic>
            );

          if (column.type === KeywordTableColumnTypes.CpcBidLow)
            return (
              <HeadCellBasic
                key={key}
                align="right"
                basicConfig={{
                  title: name,
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, undefined)}
              >
                {name}
              </HeadCellBasic>
            );

          if (column.type === KeywordTableColumnTypes.Competition)
            return (
              <HeadCellNumber
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.COMPETITION)}
                key={key}
                minFilterState={keywordTableState.filterState.competitionMin}
                maxFilterState={keywordTableState.filterState.competitionMax}
                setMinFilterState={(value) => handleFilter({ competitionMin: value })}
                setMaxFilterState={(value) => handleFilter({ competitionMax: value })}
              />
            );
          if (column.type === KeywordTableColumnTypes.SeedKeyword)
            return (
              <HeadCellText
                key={key}
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'text',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.INDIRECT_SEED)}
                filterState={keywordTableState.filterState.indirectSeed?.searchTerm ?? ''}
                setFilterState={(value) => handleFilter({ indirectSeed: { searchTerm: value } })}
              />
            );

          if (column.type === KeywordTableColumnTypes.SeedType)
            return (
              <HeadCellSelect
                key={key}
                align="left"
                basicConfig={{
                  title: name,
                  showSorting: false,
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, undefined)}
                state={keywordTableState.filterState.indirectSeedTypes}
                setState={(selectedOptions) =>
                  keywordTableState.setFilterState((prev) => ({
                    ...prev,
                    indirectSeedTypes: selectedOptions as any,
                  }))
                }
                options={[
                  { value: undefined, title: 'N/A' },
                  ...Object.keys(IndirectSeedTypeGq).map(
                    (type): GenericOption => ({
                      title: indirectSeedTypeToString(type as any) ?? 'N/A',
                      value: type,
                    })
                  ),
                ]}
              />
            );

          if (column.type === KeywordTableColumnTypes.KeywordSearch)
            return (
              <HeadCellRequest
                key={key}
                basicConfig={{
                  title: name,
                  showSorting: false,
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, undefined)}
                requests={requests}
                keywordTableState={keywordTableState}
                project={project}
                column={column}
              />
            );

          /* if (column.type === KeywordTableColumnTypes.RankStatus)
            return (
              <HeadCellRankFetchStatus
                key={key}
                keywordTableState={keywordTableState}
                basicConfig={{
                  title: name,
                  showSorting: false,
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, undefined)}
              />
            ); */
          if (column.type === KeywordTableColumnTypes.SerpFeatures)
            return (
              <HeadCellBasic
                key={key}
                align="left"
                basicConfig={{
                  title: name,
                  sortingType: 'text',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, undefined)}
              >
                {name}
              </HeadCellBasic>
            );
          if (column.type === KeywordTableColumnTypes.Rank) {
            const domain = project.domains.find((d) => d.id === column.id);
            if (!domain) return null;
            return (
              <HeadCellDomain
                key={key}
                keywordTableState={keywordTableState}
                domain={domain}
                showUrl={column.url}
                showTraffic={column.traffic}
                isProgressPage={isProgressPage}
                tableContainerRef={tableContainerRef}
              />
            );
          }

          if (column.type === KeywordTableColumnTypes.TagCount)
            return (
              <HeadCellNumber
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.TAG_COUNT)}
                key={key}
                minFilterState={keywordTableState.filterState.tagCountMin}
                maxFilterState={keywordTableState.filterState.tagCountMax}
                setMinFilterState={(value) => handleFilter({ tagCountMin: value })}
                setMaxFilterState={(value) => handleFilter({ tagCountMax: value })}
              />
            );

          if (column.type === KeywordTableColumnTypes.TagGroup) {
            const tagGroup = project.tagGroups.find((d) => d.id === column.id);
            if (!tagGroup) return null;
            return (
              <HeadCellTagGroup
                tagGroup={tagGroup}
                shownTagPanelId={
                  keywordTableState.tableUIState.showTagPanel
                    ? keywordTableState.tableUIState.shownTagPanelId
                    : undefined
                }
                setShownTagPanelId={(id?: number) =>
                  keywordTableState.setTableUIState((prev) => ({
                    ...prev,
                    showTagPanel: !!id,
                    shownTagPanelId: id,
                  }))
                }
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.TAG_GROUP)}
                key={key}
                state={keywordTableState.filterState.tagFilters}
                setState={(tagFilters) => handleFilter({ tagFilters })}
              />
            );
          }

          if (column.type === KeywordTableColumnTypes.TranslationFrom)
            return (
              <HeadCellText
                key={key}
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.TRANSLATED_WORD)}
                filterState={keywordTableState.filterState.translatedFromKeyword?.searchTerm ?? ''}
                setFilterState={(value) =>
                  handleFilter({ translatedFromKeyword: { searchTerm: value } })
                }
              />
            );

          if (column.type === KeywordTableColumnTypes.TranslationFromVolume)
            return (
              <HeadCellNumber
                key={key}
                basicConfig={{
                  title: name,
                  showSorting: true,
                  sortingType: 'number',
                  tooltip: getColumnDescription(column, project),
                }}
                basicState={getBasicState(column, KeywordSortByTypeGq.TRANSLATED_VOLUME)}
                minFilterState={keywordTableState.filterState.translatedVolumeMin}
                maxFilterState={keywordTableState.filterState.translatedVolumeMax}
                setMinFilterState={(value) => handleFilter({ translatedVolumeMin: value })}
                setMaxFilterState={(value) => handleFilter({ translatedVolumeMax: value })}
              />
            );

          return null;
        })}
        <VoxelDataTableHeadCell className="w-14" align="center">
          {page === 'dm' && (
            <AddFieldButton project={project} keywordTableState={keywordTableState} />
          )}
        </VoxelDataTableHeadCell>
        <VoxelDataTableHeadCell className="w-grow" align="left" />
      </VoxelDataTableRow>
    );
  }
);

export default HeadRow;
